// Bilingual dictionary - EN & FR
// Keep keys nested by section
export const t = (lang, path) => {
  if (!path || typeof path !== "string") return "";
  const parts = path.split(".");
  let node = dict[lang] || dict.en;
  for (const p of parts) {
    if (node === null || node === undefined) break;
    node = node[p];
  }
  if (node === undefined || node === null) {
    // fallback to english
    let fb = dict.en;
    for (const q of parts) {
      if (fb === null || fb === undefined) break;
      fb = fb[q];
    }
    return fb ?? path;
  }
  return node;
};

export const dict = {
  en: {
    nav: {
      home: "Home",
      services: "Services",
      tcf: "TCF Training",
      events: "Events",
      about: "Story",
      guides: "Guides",
      contact: "Contact",
      registry: "Sign up",
      book: "Book a call",
      login: "Client portal",
      dashboard: "My space",
      logout: "Sign out",
      admin: "Admin",
    },
    hero: {
      overline: "French-speaking world → Canada, guided.",
      title: "Your Canadian journey,",
      title_italic: "on your own terms.",
      sub: "Educational guidance from someone who's walked the path. I've accompanied 14 candidates — with 100% Permanent Resident visa success (scores between 382 and 456) in Cameroon and Ivory Coast. No middlemen. Just clarity.",
      cta_primary: "Start with a call",
      cta_secondary: "Explore TCF training",
      stat_people: "people empowered",
      stat_couples: "couples supported",
      stat_lang: "bilingual, always",
    },
    services: {
      title: "How I can help",
      subtitle: "Every service is designed to teach you the process — not replace you in it.",
      pr: { name: "Permanent Residence", desc: "Express Entry, PNP pathways, CRS score strategy, document prep." },
      visitor: { name: "Visitor Visa", desc: "Purpose-of-visit, financial proof, letters, refusal recovery." },
      student: { name: "Study Permit", desc: "DLI selection, GIC, SDS stream, PAL letters." },
      common: { name: "Common-Law & Spouse", desc: "Proof of relationship, sponsorship, timing strategy for couples." },
      tcf: { name: "TCF Canada", desc: "Structured French prep — reach your target score." },
      learn_more: "Learn more",
    },
    tcf: {
      overline: "Test de Connaissance du Français",
      title: "TCF Canada — trained to pass.",
      sub: "Structured, bilingual TCF preparation for Express Entry and Canadian citizenship. Master the format, then master the score.",
      badge_students: "students trained",
      section_modules: "What's inside",
      buy: "Enroll now",
      free_intro: "Free intro lesson",
    },
    events: {
      title: "Live sessions",
      subtitle: "Webinars, Q&A, and small-group assistance sessions.",
      register: "Register",
      full: "Full",
      free: "Free",
      upcoming: "Upcoming",
      none: "No events yet — check back soon.",
    },
    about: {
      overline: "The story",
      title: "I've been where you are.",
      body_1: "I’m Josias Ndam. My journey from Cameroon to Canada showed me how scattered and difficult to verify immigration information can feel when preparing a project from abroad.",
      body_2: "I started by helping people close to me understand the steps, organize documents and track their progress. That experience later expanded to individuals, couples and families from several French-speaking countries.",
      body_3: "JNIC provides general information, educational preparation and organizational support. JNIC is not an authorized immigration representative and does not provide legal representation or legal advice.",
      values_title: "What I believe",
      values: [
        { k: "Empowerment", v: "Teach the process, don't sell the fear." },
        { k: "Bilingual by default", v: "Every resource in EN and FR." },
        { k: "No shortcuts", v: "Documents win cases. Not promises." },
        { k: "Honesty", v: "If your file isn't strong yet, I'll tell you." },
      ],
    },
    contact: {
      title: "Get in touch",
      sub: "Send a message. I read every one.",
      name: "Your name",
      email: "Email",
      subject: "Subject",
      message: "How can I help?",
      send: "Send message",
      sent: "Thank you — I'll be in touch.",
      whatsapp: "or WhatsApp me",
    },
    booking: {
      title: "Book an orientation call",
      sub: "Choose a slot and I'll confirm within 24 hours.",
      topic: "What's this about?",
      notes: "Anything I should know?",
      confirm: "Confirm booking",
      login_required: "Please sign in to book",
      confirmed: "Booking confirmed — check your email.",
    },
    portal: {
      welcome: "Welcome",
      my_bookings: "My bookings",
      my_courses: "My courses",
      no_bookings: "No bookings yet.",
      no_courses: "No courses enrolled yet.",
      view_courses: "Browse TCF courses",
    },
    chat: {
      title: "Ask JN AI",
      placeholder: "Ask about visas, TCF, or the process...",
      disclaimer: "Informational only — not legal advice.",
      send: "Send",
      open: "Open assistant",
      close: "Close",
    },
    footer: {
      tagline: "Clear information for your Canadian project.",
      sitemap: "Sitemap",
      legal: "Legal",
      terms: "Terms",
      privacy: "Privacy",
      disclaimer: "JNIC is not an authorized immigration representative and does not provide legal representation. Informational, educational and organizational support only.",
      copy: "© 2026 JN Immigration Consulting. Built with care.",
      newsletter: "Get the monthly guide",
      subscribe: "Subscribe",
      subscribed: "Subscribed — merci!",
      community_title: "Join the community",
      community_sub: "A free WhatsApp group for anyone on the immigration journey to Canada — questions, tips, testimonials, and mutual support.",
      community_cta: "Join the WhatsApp group",
    },
    common: {
      loading: "Loading…",
      error: "Something went wrong.",
      back: "Back",
      cad: "CAD",
    },
    registry: {
      title_talent: "Join the JNIC talent registry",
      overline_talent: "Talent Registry",
      sub_talent: "Bachelor's, Master's, or PhD holders with limited funds — register your profile. Josias periodically shares opportunities, scholarships, and pathway-fit advice for people in the registry.",
      title_couples: "Couples registry",
      overline_couples: "Common-law & married couples",
      sub_couples: "Confidential registry for couples exploring Canadian sponsorship (inland, outland, common-law, married). Only Josias can view this data.",
      submit: "Submit my profile",
      submitting: "Submitting…",
      done_title: "You're in.",
      done_talent: "Your profile is in the registry. I'll reach out when a relevant opportunity fits.",
      done_couples: "Your couple file is saved. Josias will review it and reach out with next steps.",
      switch_couples: "You're a couple? Register together →",
      switch_talent: "Individual profile? Register here →",
      consent: "I consent to JNIC storing this information and contacting me about opportunities.",
      privacy: "Confidential — Josias-only access.",
      fields: {
        first_name: "First name",
        last_name: "Last name",
        email: "Email",
        phone: "Phone (optional)",
        country_of_residence: "Country of residence",
        city: "City",
        nationality: "Nationality",
        date_of_birth: "Date of birth",
        education_level: "Highest education",
        education_bachelor: "Bachelor's",
        education_master: "Master's",
        education_phd: "PhD",
        education_other: "Other",
        field_of_study: "Field of study",
        institution: "Institution",
        graduation_year: "Graduation year",
        years_of_experience: "Years of experience",
        profession: "Profession / role",
        professional_summary: "Professional summary",
        has_passport: "Do you have a valid passport?",
        yes: "Yes",
        no: "No",
        passport_expiry: "Passport expiry",
        french_level: "French level",
        english_level: "English level",
        target_pathway: "Target Canadian pathway",
        pathway_pr: "Permanent Residence",
        pathway_study: "Study",
        pathway_work: "Work permit",
        pathway_other: "Other / undecided",
        financial_situation: "Financial situation",
        fin_constrained: "Very constrained",
        fin_limited: "Limited",
        fin_ok: "Comfortable enough",
        notes: "Anything else you'd like Josias to know",
        // couples
        partner_a: "You",
        partner_b: "Your partner",
        relationship_status: "Relationship status",
        rel_common_law: "Common-law",
        rel_married: "Married",
        rel_engaged: "Engaged",
        relationship_start_date: "Relationship start",
        cohabitation_start_date: "Living together since",
        children: "Do you have children together?",
        number_of_children: "Number of children",
        sponsorship_type: "Sponsorship type",
        sp_inland: "Inland",
        sp_outland: "Outland",
        sp_undecided: "Undecided",
        documents_ready: "Documents ready",
        doc_joint_lease: "Joint lease",
        doc_joint_bank: "Joint bank account",
        doc_joint_insurance: "Joint insurance",
        doc_photos: "Photos together",
        doc_communication: "Communication history",
      },
    },
    admin: {
      title: "Admin",
      forbidden: "Admin access only.",
      tab_documents: "Client Documents",
      tab_estimates: "Estimates & Receipts",
      tab_inquiries: "Inquiries",
      tab_talent: "Talent Registry",
      tab_couples: "Couples Registry",
      tab_bookings: "Bookings",
      tab_events: "Events",
      tab_messages: "Messaging",
      tab_settings: "Settings",
      empty: "Nothing yet.",
      stat_total: "Total",
      stat_passport: "With passport",
      stat_bachelor: "Bachelor's",
      stat_master: "Master's",
      stat_phd: "PhD",
    },
  },
  fr: {
    nav: {
      home: "Accueil",
      services: "Services",
      tcf: "Formation TCF",
      events: "Événements",
      about: "L'histoire",
      guides: "Guides",
      contact: "Contact",
      registry: "S'enregistrer",
      book: "Réserver un appel",
      login: "Espace client",
      dashboard: "Mon espace",
      logout: "Déconnexion",
      admin: "Admin",
    },
    hero: {
      overline: "Monde francophone → Canada, guidé.",
      title: "Votre procédure d'immigration",
      title_italic: "pour le Canada, à votre rythme.",
      sub: "L'accompagnement pédagogique de quelqu'un qui a fait le chemin. J'ai accompagné 14 candidats — avec 100% de visas de Résident permanent obtenus (scores entre 382 et 456) au Cameroun et en Côte d'Ivoire. Pas d'intermédiaires coûteux. Juste de la clarté.",
      cta_primary: "Commencer par un appel",
      cta_secondary: "Découvrir la formation TCF",
      stat_people: "personnes accompagnées",
      stat_couples: "couples soutenus",
      stat_lang: "bilingue, toujours",
    },
    services: {
      title: "Comment je peux vous aider",
      subtitle: "Chaque service vise à vous enseigner le processus — pas à vous remplacer dans votre dossier.",
      pr: { name: "Résidence Permanente", desc: "Entrée Express, Programme des Candidats des Provinces (PCP), stratégie CRS, préparation du dossier documentaire." },
      visitor: { name: "Visa Visiteur", desc: "Motif de visite, preuves financières, lettres, recours après refus." },
      student: { name: "Permis d'études", desc: "Choix de l'EED, CPG, volet SDS, lettres PAL." },
      common: { name: "Union de fait & Parrainage conjoint", desc: "Preuve de relation, parrainage, planification du dossier pour les couples." },
      tcf: { name: "TCF Canada", desc: "Préparation structurée au TCF Canada — atteignez votre score cible en français." },
      learn_more: "En savoir plus",
    },
    tcf: {
      overline: "Test de Connaissance du Français",
      title: "TCF Canada — préparez-vous pour réussir.",
      sub: "Préparation TCF Canada structurée et bilingue pour l'Entrée Express et la citoyenneté canadienne. Maîtrisez le format, puis maîtrisez le score.",
      badge_students: "étudiants formés",
      section_modules: "Ce qu'il y a dedans",
      buy: "S'inscrire",
      free_intro: "Leçon d'introduction gratuite",
    },
    events: {
      title: "Sessions live",
      subtitle: "Webinaires, Q&R, et sessions d'assistance en petit groupe.",
      register: "S'inscrire",
      full: "Complet",
      free: "Gratuit",
      upcoming: "À venir",
      none: "Pas encore d'événements — revenez bientôt.",
    },
    about: {
      overline: "L'histoire",
      title: "Je suis passé par là.",
      body_1: "Je m’appelle Josias Ndam. Mon parcours du Cameroun vers le Canada m’a montré combien l’information en immigration peut sembler dispersée et difficile à vérifier lorsqu’on prépare un projet depuis l’étranger.",
      body_2: "J’ai commencé par aider des proches à comprendre les étapes, organiser leurs documents et suivre leur progression. Cette expérience s’est ensuite élargie à des personnes, des couples et des familles de plusieurs pays francophones.",
      body_3: "JNIC fournit de l’information générale, de la préparation pédagogique et du soutien organisationnel. JNIC n’est pas un représentant autorisé en immigration et ne fournit ni représentation juridique ni avis juridique.",
      values_title: "Ce en quoi je crois",
      values: [
        { k: "Autonomisation", v: "Enseigner le processus, pas vendre la peur." },
        { k: "Bilingue par défaut", v: "Chaque ressource en anglais et en français." },
        { k: "Aucun raccourci", v: "Les documents gagnent les dossiers. Pas les promesses." },
        { k: "Honnêteté", v: "Si votre dossier n'est pas prêt, je vous le dirai." },
      ],
    },
    contact: {
      title: "Écrivez-moi",
      sub: "Envoyez un message. Je les lis tous.",
      name: "Votre nom",
      email: "Courriel",
      subject: "Sujet",
      message: "Comment puis-je aider ?",
      send: "Envoyer",
      sent: "Merci — je vous recontacte.",
      whatsapp: "ou WhatsApp",
    },
    booking: {
      title: "Réserver un appel d’orientation",
      sub: "Choisissez un créneau, je confirme sous 24 heures.",
      topic: "Sujet ?",
      notes: "Autre chose à savoir ?",
      confirm: "Confirmer",
      login_required: "Connectez-vous pour réserver",
      confirmed: "Réservation confirmée — vérifiez vos courriels.",
    },
    portal: {
      welcome: "Bienvenue",
      my_bookings: "Mes réservations",
      my_courses: "Mes cours",
      no_bookings: "Aucune réservation.",
      no_courses: "Aucun cours suivi.",
      view_courses: "Parcourir les cours TCF",
    },
    chat: {
      title: "Demander à JN AI",
      placeholder: "Visas, TCF, processus…",
      disclaimer: "Informations générales — pas d'avis juridique.",
      send: "Envoyer",
      open: "Ouvrir l'assistant",
      close: "Fermer",
    },
    footer: {
      tagline: "Une information claire pour votre projet canadien.",
      sitemap: "Plan du site",
      legal: "Légal",
      terms: "CGU",
      privacy: "Confidentialité",
      disclaimer: "JNIC n’est pas un représentant autorisé en immigration et ne fournit pas de représentation juridique. Soutien informatif, pédagogique et organisationnel uniquement.",
      copy: "© 2026 JN Immigration Consulting. Fait avec soin.",
      newsletter: "Recevez le guide mensuel",
      subscribe: "S'abonner",
      subscribed: "Abonné — merci !",
      community_title: "Rejoignez la communauté",
      community_sub: "Un groupe WhatsApp gratuit pour toute personne sur le parcours d'immigration au Canada — questions, astuces, témoignages, entraide.",
      community_cta: "Rejoindre le groupe WhatsApp",
    },
    common: {
      loading: "Chargement…",
      error: "Une erreur est survenue.",
      back: "Retour",
      cad: "CAD",
    },
    registry: {
      title_talent: "Rejoindre le registre des talents JNIC",
      overline_talent: "Registre des talents",
      sub_talent: "Titulaires d'un Bachelor, Master ou Doctorat avec fonds limités — enregistrez votre profil. Josias partage régulièrement des opportunités, bourses et conseils personnalisés aux personnes du registre.",
      title_couples: "Registre des couples",
      overline_couples: "Union de fait & couples mariés",
      sub_couples: "Registre confidentiel pour les couples explorant le parrainage canadien (intérieur, extérieur, union de fait, mariés). Seul Josias peut consulter ces données.",
      submit: "Soumettre mon profil",
      submitting: "Envoi…",
      done_title: "C'est fait.",
      done_talent: "Votre profil est enregistré. Je vous contacterai quand une opportunité pertinente apparaît.",
      done_couples: "Votre dossier de couple est enregistré. Josias vous contactera pour les prochaines étapes.",
      switch_couples: "Vous êtes en couple ? Inscription conjointe →",
      switch_talent: "Profil individuel ? Inscrivez-vous ici →",
      consent: "Je consens à ce que JNIC conserve ces informations et me contacte pour des opportunités.",
      privacy: "Confidentiel — accès réservé à Josias.",
      fields: {
        first_name: "Prénom",
        last_name: "Nom",
        email: "Courriel",
        phone: "Téléphone (facultatif)",
        country_of_residence: "Pays de résidence",
        city: "Ville",
        nationality: "Nationalité",
        date_of_birth: "Date de naissance",
        education_level: "Niveau d'études le plus élevé",
        education_bachelor: "Licence / Bachelor",
        education_master: "Master",
        education_phd: "Doctorat",
        education_other: "Autre",
        field_of_study: "Domaine d'études",
        institution: "Établissement",
        graduation_year: "Année d'obtention",
        years_of_experience: "Années d'expérience",
        profession: "Profession / poste",
        professional_summary: "Résumé professionnel",
        has_passport: "Avez-vous un passeport valide ?",
        yes: "Oui",
        no: "Non",
        passport_expiry: "Expiration du passeport",
        french_level: "Niveau de français",
        english_level: "Niveau d'anglais",
        target_pathway: "Voie canadienne visée",
        pathway_pr: "Résidence Permanente",
        pathway_study: "Études",
        pathway_work: "Permis de travail",
        pathway_other: "Autre / non décidé",
        financial_situation: "Situation financière",
        fin_constrained: "Très limitée",
        fin_limited: "Limitée",
        fin_ok: "Suffisante",
        notes: "Autre chose à savoir",
        partner_a: "Vous",
        partner_b: "Votre partenaire",
        relationship_status: "Statut de la relation",
        rel_common_law: "Union de fait",
        rel_married: "Marié(e)",
        rel_engaged: "Fiancé(e)",
        relationship_start_date: "Début de la relation",
        cohabitation_start_date: "Cohabitation depuis",
        children: "Avez-vous des enfants ensemble ?",
        number_of_children: "Nombre d'enfants",
        sponsorship_type: "Type de parrainage",
        sp_inland: "Intérieur",
        sp_outland: "Extérieur",
        sp_undecided: "Non décidé",
        documents_ready: "Documents prêts",
        doc_joint_lease: "Bail conjoint",
        doc_joint_bank: "Compte bancaire conjoint",
        doc_joint_insurance: "Assurance conjointe",
        doc_photos: "Photos ensemble",
        doc_communication: "Historique de communication",
      },
    },
    admin: {
      title: "Admin",
      forbidden: "Accès administrateur uniquement.",
      tab_documents: "Documents Clients",
      tab_estimates: "Devis & Reçus",
      tab_inquiries: "Demandes",
      tab_talent: "Registre Talents",
      tab_couples: "Registre Couples",
      tab_bookings: "Réservations",
      tab_events: "Événements",
      tab_messages: "Messagerie",
      tab_settings: "Paramètres",
      empty: "Rien pour l'instant.",
      stat_total: "Total",
      stat_passport: "Avec passeport",
      stat_bachelor: "Licence",
      stat_master: "Master",
      stat_phd: "Doctorat",
    },
  },
};
